/**
 * Purpose: Main message bubble component that wraps message content and context menu
 *
 * How it works:
 * 1. Renders the message content with appropriate styling
 * 2. Provides a context menu trigger wrapper
 * 3. Delegates all interaction handling to the MessageContextMenu component
 *
 * Key features:
 * - Lightweight presentation component
 * - Context menu integration
 * - Content rendering for different message types
 * - Emoji detection and special formatting
 * - Clean separation of concerns with MessageContextMenu
 */

// import { useState } from "react";
import { cn } from "@/lib/utils";
import { MessageEntity } from "@/indexedDB/types";
import {
  ContextMenu,
  ContextMenuTrigger,
} from "@/components/primitives/context-menu";
import { extractEmojiFromMessage } from "@/utils/messageUtils";
import MessageContextMenu from "./MessageContextMenu";
import MessageBubbleContent from "./MessageBubbleContent";

interface MessageBubbleProps {
  message: MessageEntity;
}

export default function MessageBubble({ message }: MessageBubbleProps) {
  const { isEmojiOnlyMessage } = extractEmojiFromMessage(message.content.text);

  // Debug ring + hue cycle by epoch (golden-angle distribution → distinct hues)
  const debugEpoch = import.meta.env.DEV ? message.keyEpoch : null;
  const epochHue = debugEpoch != null ? (debugEpoch * 137.508) % 360 : null;
  const debugStyle =
    epochHue != null
      ? { boxShadow: `0 0 0 2px hsl(${epochHue} 75% 55%)` }
      : undefined;

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger
        style={debugStyle}
        className={cn(
          "relative grid w-full max-w-[420px] gap-1 overflow-visible rounded-md p-1 lg:max-w-[520px]",
          message.isOutgoing
            ? "bg-primary text-right text-primary-foreground"
            : "bg-accent text-left text-accent-foreground",
          isEmojiOnlyMessage ? "!bg-transparent" : "",
        )}
      >
        {epochHue != null && (
          <span
            className="rounded font-mono pointer-events-none absolute -right-1 -top-2 bg-background px-1 text-[10px] leading-tight"
            style={{ color: `hsl(${epochHue} 75% 45%)` }}
          >
            ε{debugEpoch}
          </span>
        )}
        <MessageBubbleContent message={message} />
      </ContextMenuTrigger>

      <MessageContextMenu message={message} />
    </ContextMenu>
  );
}
